// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { InvitationCreateForm } from "@/components/superadmin/invitations/InvitationCreateForm";
import { InvitationsTable } from "@/components/superadmin/invitations/InvitationsTable";
import { SuperadminService } from "@/services/superadmin";

export interface SearchParams {
  page: number;
}

export const Route = createFileRoute("/superadmin/invitations")({
  component: InvitationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
  }),
});

function InvitationsPage() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const params = Route.useSearch();

  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "planInvitations", params],
    queryFn: () =>
      SuperadminService.listPlanInvitations({ page: params.page, limit: 20 }),
  });

  const handlePageChange = useCallback(
    (page: number) => {
      navigate({ to: "/superadmin/invitations", search: { page }, replace: true });
    },
    [navigate],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("invitations.title")}</h1>
        <p className="text-muted-foreground mt-1">
          {t("invitations.description")}
        </p>
      </div>

      <InvitationCreateForm />

      <InvitationsTable
        invitations={data?.invitations ?? []}
        pagination={data?.pagination}
        isLoading={isLoading}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
